import { useEffect, useState } from 'react';
import { ArrowDownOutlined, ArrowLeftOutlined, ArrowUpOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Popconfirm,
  Row,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link, useParams } from 'react-router-dom';

import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { get, post } from 'core/fetch';

const { Title } = Typography;

interface ApiResponse<T> {
  valid: boolean;
  errorMessage?: string;
  data?: T;
}

type ActionType = 'CREATE_ONLY' | 'UPDATE_ONLY' | 'CREATE_UPDATE';
type ImportStatus = 'COMPARE' | 'DONE';
type ItemAction = 'CREATE' | 'UPDATE' | 'SKIP' | 'ERROR';

interface StudentImportRow {
  id: number;
  action_type: ActionType;
  status: ImportStatus;
  total_record: number;
  created_record: number;
  updated_record: number;
  skipped_record: number;
}

interface StudentImportItemRow {
  id: number;
  register_no: string;
  last_name: string;
  first_name: string;
  family_name: string;
  class_name: string;
  action: ItemAction;
  changes: string | null;
  message: string | null;
}

const ACTION_TAGS: Record<ItemAction, { color: string; label: string }> = {
  CREATE: { color: 'green', label: 'Шинээр нэмэгдэнэ' },
  UPDATE: { color: 'blue', label: 'Шинэчлэгдэнэ' },
  SKIP: { color: 'default', label: 'Алгасагдана' },
  ERROR: { color: 'red', label: 'Алдаатай' },
};

function formatChanges(raw: string | null): string {
  if (!raw) return '—';
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    return Object.entries(parsed)
      .map(([field, change]) => `${field}: ${change}`)
      .join('\n');
  } catch {
    return raw;
  }
}

const StudentImportCompare = () => {
  const { appKey, id } = useParams<{ appKey: string; id: string }>();

  const [studentImport, setStudentImport] = useState<StudentImportRow | null>(null);
  const [items, setItems] = useState<StudentImportItemRow[]>([]);
  const skipCount = items.filter((item) => item.action === 'SKIP').length;
  const errorCount = items.filter((item) => item.action === 'ERROR').length;
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      get<StudentImportRow>(`/student_import/${id}`),
      post<StudentImportItemRow[]>('/student_import_item/search', {
        $where: { student_import_id: Number(id) },
        $sort: { id: 'asc' },
      }),
    ])
      .then(([importRow, itemRows]) => {
        setStudentImport(importRow);
        setItems(itemRows);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSync = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      const response = await post<ApiResponse<{ created: number; updated: number }>>(
        '/custom/student-import/sync',
        { student_import_id: Number(id) },
      );

      if (!response.valid) {
        message.error(response.errorMessage ?? 'Sync хийхэд алдаа гарлаа.');
        return;
      }

      message.success(
        `Амжилттай: ${response.data?.created ?? 0} шинээр нэмэгдэж, ${response.data?.updated ?? 0} шинэчлэгдлээ.`,
      );
      load();
    } catch (err: any) {
      message.error(err?.error ?? err?.errorMessage ?? err?.message ?? 'Sync хийхэд алдаа гарлаа.');
    } finally {
      setSyncing(false);
    }
  };

  const columns: ColumnsType<StudentImportItemRow> = [
    { title: 'Регистер', dataIndex: 'register_no', width: 140 },
    {
      title: 'Овог нэр',
      render: (_, row) => `${row.last_name ?? ''} ${row.first_name ?? ''}`.trim() || '—',
    },
    { title: 'Анги', dataIndex: 'class_name', width: 100 },
    {
      title: 'Үйлдэл',
      dataIndex: 'action',
      width: 160,
      filters: (Object.keys(ACTION_TAGS) as ItemAction[]).map((action) => ({
        text: ACTION_TAGS[action].label,
        value: action,
      })),
      onFilter: (value, row) => row.action === value,
      render: (value: ItemAction) => (
        <Tag color={ACTION_TAGS[value].color}>{ACTION_TAGS[value].label}</Tag>
      ),
    },
    {
      title: 'Өөрчлөлт / Тайлбар',
      render: (_, row) => (
        <span style={{ whiteSpace: 'pre-line' }}>{row.message || formatChanges(row.changes)}</span>
      ),
    },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card loading={loading && !studentImport}>
        <Link to={`/apps/${appKey}/import_students`}>
          <ArrowLeftOutlined /> Импортын жагсаалт руу буцах
        </Link>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '16px 0',
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Харьцуулах
          </Title>

          <Popconfirm
            title="Sync хийх үү?"
            description="Дээрх CREATE/UPDATE мөрүүд бодит сурагчийн бүртгэлд тусгагдана. Буцаах боломжгүй."
            okText="Тийм, Sync хийх"
            cancelText="Болих"
            onConfirm={handleSync}
            disabled={studentImport?.status !== 'COMPARE'}
          >
            <Button type="primary" loading={syncing} disabled={studentImport?.status !== 'COMPARE'}>
              {studentImport?.status === 'DONE' ? 'Sync хийгдсэн' : 'Sync'}
            </Button>
          </Popconfirm>
        </div>

        {studentImport && (
          <Row
            gutter={16}
            style={{ marginBottom: 24, background: '#f5f5f7', padding: 16, borderRadius: 12 }}
          >
            <Col flex={1}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Нийт"
                  value={studentImport.total_record}
                  valueStyle={{ color: '#262626' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Шинээр нэмэгдэх"
                  value={studentImport.created_record}
                  valueStyle={{ color: '#389e0d' }}
                  prefix={studentImport.created_record > 0 ? <ArrowUpOutlined /> : undefined}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Шинэчлэгдэх"
                  value={studentImport.updated_record}
                  valueStyle={{ color: '#1677ff' }}
                  prefix={studentImport.updated_record > 0 ? <ArrowUpOutlined /> : undefined}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Алдаатай"
                  value={errorCount}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={errorCount > 0 ? <ArrowDownOutlined /> : undefined}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Алгасагдана"
                  value={skipCount}
                  valueStyle={{ color: '#8c8c8c' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 50 }}
        />
      </Card>
    </LayoutWithMenu>
  );
};

export default StudentImportCompare;
